import { createAuthenticatedClient } from "../authenticatedClient";
import { API_BASE_URL } from "../config";
import { safeValidate } from "../safeValidate";
import {
  InventoryAssetDetail,
  inventoryAssetDetailSchema,
  FindingsBySeverityResponse,
  findingsBySeverityResponseSchema,
  TimelineEventResponse,
  timelineEventResponseSchema,
  AssetTimelineFilter,
  OwnersFiltersValueResponse,
  ownersFiltersValueResponseSchema,
  OwnersFiltersListResponse,
  ownersfiltersListResponseSchema,
  OwnersListResponse,
  ownersListResponseSchema,
  PreviouslyAssignedOwnersResponse,
  previouslyAssignedOwnersResponseSchema,
  AssignOwnerResponse,
  assignOwnerResponseSchema,
} from "../schemas/policiesInventory.schema";

const policiesInventoryClient = createAuthenticatedClient(
  `${API_BASE_URL}/nhi`,
);

export interface AssetTimelineRequestPayload {
  searchString: string;
  sortBy: string;
  sortOrder: string;
  filters: AssetTimelineFilter[];
}

export interface FiltersValuesRequest {
  page: string;
  field: string;
  searchText: string;
}

export interface OwnersListRequest {
  sortBy?: string | null;
  sortOrder?: string | null;
  filters: {
    name: string;
    operator: string;
    value: string;
  }[];
  assetId?: string;
}

export interface AssignOwnerToSingleAssetRequest {
  assetId: string;
  userIds: string[];
}

export interface AssignOwnerToBulkAssetsRequest {
  assetIds: string[];
  userIds: string[];
}

export interface AssignOwnerToAllAssetsRequest {
  assetFilters: {
    name: string;
    operator: string;
    value: string;
  }[];
  userIds: string[];
}

export const policiesInventoryApi = {
  inventorySummaryData: async (
    assetId: string,
  ): Promise<InventoryAssetDetail> => {
    const data = await policiesInventoryClient.get<unknown>(
      `/v2/asset/summary?id=${assetId}`,
    );
    const validated = safeValidate(
      inventoryAssetDetailSchema,
      data,
      "inventoryAssetDetail",
    ) as InventoryAssetDetail;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  inventoryFindigsData: async (
    assetId: string,
  ): Promise<FindingsBySeverityResponse> => {
    const data = await policiesInventoryClient.post<unknown>(
      "/v1/asset/findings",
      {
        assetId,
      },
    );
    const validated = safeValidate(
      findingsBySeverityResponseSchema,
      data,
      "inventoryFindigsData",
    ) as FindingsBySeverityResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  inventoryTimelineData: async ({
    requestPayload,
    pageParam,
    assetId,
    pageSize,
  }: {
    requestPayload: AssetTimelineRequestPayload;
    pageParam: string | null;
    assetId: number;
    pageSize?: number;
  }): Promise<TimelineEventResponse> => {
    let url = "/v1/asset/{id}/timeline".replace("{id}", String(assetId));

    if (pageSize) {
      const separator = url.includes("?") ? "&" : "?";

      url += `${separator}pageSize=${pageSize}`;
    }
    const data = await policiesInventoryClient.post<unknown>(url, {
      ...requestPayload,
      nextPageToken: pageParam,
    });
    const validated = safeValidate(
      timelineEventResponseSchema,
      data,
      "inventoryTimelineData",
    ) as TimelineEventResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  getAssignOwnerFiltersValues: async ({
    payload,
    pageParam,
  }: {
    payload: FiltersValuesRequest;
    pageParam: string | null;
  }) => {
    const data = await policiesInventoryClient.post<OwnersFiltersValueResponse>(
      "nhi/v1/filters/values",
      { ...payload, nextPageToken: pageParam },
    );
    const validated = safeValidate(
      ownersFiltersValueResponseSchema,
      data,
      "getAssignOwnerFiltersValues",
    ) as OwnersFiltersValueResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  getOwnersList: async ({
    payload,
    pageParam,
  }: {
    payload: OwnersListRequest;
    pageParam: string | null;
  }): Promise<OwnersListResponse> => {
    const data = await policiesInventoryClient.post<unknown>("/v1/users", {
      ...payload,
      nextPageToken: pageParam,
    });
    const validated = safeValidate(
      ownersListResponseSchema,
      data,
      "getOwnersList",
    ) as OwnersListResponse;
    return validated;
  },
  getPreviouslyAssignedOwners: async (
    assetId: string,
  ): Promise<PreviouslyAssignedOwnersResponse> => {
    const data = await policiesInventoryClient.get<unknown>(
      `/v1/asset/owners?assetId=${assetId}`,
    );
    const validated = safeValidate(
      previouslyAssignedOwnersResponseSchema,
      data,
      "getPreviouslyAssignedOwners",
    ) as PreviouslyAssignedOwnersResponse;
    return validated;
  },
  assignOwnerToSingleAsset: async (
    params: AssignOwnerToSingleAssetRequest,
  ): Promise<AssignOwnerResponse> => {
    const data = await policiesInventoryClient.post<unknown>(
      "/v1/asset/owners",
      params,
    );
    const validated = safeValidate(
      assignOwnerResponseSchema,
      data,
      "assignOwnerToSingleAsset",
    ) as AssignOwnerResponse;
    return validated;
  },
  assignOwnerToBulkAssets: async (
    params: AssignOwnerToBulkAssetsRequest,
  ): Promise<AssignOwnerResponse> => {
    const data = await policiesInventoryClient.post<unknown>(
      "/v1/assets/owners",
      params,
    );
    const validated = safeValidate(
      assignOwnerResponseSchema,
      data,
      "assignOwnerToBulkAssets",
    ) as AssignOwnerResponse;
    return validated;
  },
  assignOwnerToAllAssets: async (
    params: AssignOwnerToAllAssetsRequest,
  ): Promise<AssignOwnerResponse> => {
    const data = await policiesInventoryClient.post<unknown>(
      "v1/assets/owners/bulk",
      params,
    );
    const validated = safeValidate(
      assignOwnerResponseSchema,
      data,
      "assignOwnerToAllAssets",
    ) as AssignOwnerResponse;
    return validated;
  },
  getAssignOwnerFiltersList: async () => {
    const url = `/v1/filters?page=nhiOwnershipDialogueBox`;
    const data =
      await policiesInventoryClient.get<OwnersFiltersListResponse>(url);
    const validated = safeValidate(
      ownersfiltersListResponseSchema,
      data,
      "getAssignOwnerFiltersList",
    ) as OwnersFiltersListResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
};
